/**
 * Log or edit one interaction — a call, a meeting, an email or a note.
 *
 * Shared rather than pipeline-owned: the same dialog serves a contact, an
 * organization, a sales partner and a deal. The deal-only behaviour (offering to
 * link a newly-named contact to the deal) is gated on there being a deal, so a
 * tenant with Pipeline switched off never sees it.
 */
import { useContext, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Checkbox } from '../../../components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import { BidiText, CompactRichTextEditor, DialogForm, MultiContactPicker } from '../common';
import { LocalizedDateTimeInput } from '../index';
import { useNotification } from '../../context';
import { useAddContactToDeal } from '../../pipeline/hooks/useDeals';
import type { Interaction } from '../../../generated/api/models/interaction';
import type { InteractionCreate } from '../../../generated/api/models/interactionCreate';
import type { InteractionUpdate } from '../../../generated/api/models/interactionUpdate';
import type { InteractionCreatableType } from '../../../generated/api/models/interactionCreatableType';
import type { InteractionEditableType } from '../../../generated/api/models/interactionEditableType';
import type { Contact } from '../../../modules/master_data/contacts/types';
import { getContactDisplayName } from '../../../modules/master_data/contacts/utils/contactName';
import {
  CUSTOM_INTERACTION_TYPE,
  OFFERED_BUILTIN_INTERACTION_TYPES,
} from './interactionTypes';
import { AuthContext } from '../../auth/context';
import { isModuleAdminForUser } from '../../hooks/useIsModuleAdmin';
import {
  CONTACTS_MODULE_SETTINGS_PATH,
  resolveInteractionTypeLabel,
  selectableInteractionTypes,
  useInteractionTypes,
} from '../../../modules/master_data/contacts/hooks/useInteractionTypes';
import { useEditInteraction, useLogInteraction, type InteractionSubjectType } from './useInteractions';
import type { DealStageType } from '@/generated/config-enums';

interface InteractionFormDialogProps {
  open: boolean;
  onClose: () => void;
  subjectType: InteractionSubjectType;
  subjectId: string;
  contactId?: string;
  organizationId?: string | null;
  companyName?: string | null;
  dealId?: string;
  /** Contacts already linked to the deal — surfaced as the first group in the picker. */
  dealContacts?: Contact[];
  /** Deal stage type — drives default relationships for quick-created contacts. */
  dealStageType?: DealStageType | null;
  /** When provided, the form edits this interaction instead of logging a new one. */
  interaction?: Interaction | null;
}

export function InteractionFormDialog({
  open,
  onClose,
  subjectType,
  subjectId,
  contactId,
  organizationId,
  companyName,
  dealId,
  dealContacts,
  dealStageType,
  interaction,
}: InteractionFormDialogProps) {
  const { t, i18n } = useTranslation('contacts');
  const { showSuccess, showError } = useNotification();
  const logInteraction = useLogInteraction();
  const editInteraction = useEditInteraction();
  const addContactToDeal = useAddContactToDeal();

  const isEditMode = !!interaction;
  const effectiveDealId = dealId || (subjectType === 'deal' ? subjectId : undefined);
  const dealContactIds = useMemo(
    () => new Set((dealContacts ?? []).map((c) => c.id)),
    [dealContacts],
  );

  const [interactionType, setInteractionType] = useState<string>(
    interaction?.activity_type ?? 'note',
  );
  // The type the user picked when it is one of the TENANT's, held beside
  // `interactionType` (which then carries the `custom` discriminator) because the
  // two are one fact spelled in two columns and the API refuses half of it.
  const [tenantTypeId, setTenantTypeId] = useState<string | null>(
    interaction?.interaction_type_id ?? null,
  );

  // Fetch EVERYTHING, then narrow. A retired type may not receive new activity (the
  // backend refuses it), but this dialog also EDITS an interaction already logged under
  // one — and an active-only fetch cannot be un-filtered here, so that row would open
  // with an empty type field while the type is still stored on it.
  const { data: tenantTypeData } = useInteractionTypes({ includeInactive: true });
  const tenantTypes = useMemo(
    () => selectableInteractionTypes(tenantTypeData?.types, tenantTypeId),
    [tenantTypeData, tenantTypeId]
  );
  // The picker's link to where types are DEFINED — offered only to a caller the write
  // routes would admit, so it never points at a page that will refuse them.
  //
  // Read through the CONTEXT rather than `useIsModuleAdmin()`, which throws when no
  // AuthProvider is mounted. This dialog has four hosts across two modules, and a
  // hard throw for the sake of an optional LINK would make all of them unmountable
  // outside the app shell. No provider means no user, which means no link — the
  // conservative answer, and the one that cannot show an action the API refuses.
  //
  // The scope is `contacts`, master_data's contacts half — the same one the three
  // interaction-type write routes take and the same one
  // `/admin/module-settings/contacts` guards. It must be the PERMISSION-module name
  // and not `master_data`: asking the module-wide question would show the link to a
  // Products Admin, whom both the page and the API refuse.
  const auth = useContext(AuthContext);
  const canManageTypes = isModuleAdminForUser(auth?.user ?? null, 'contacts');

  /** Pick a built-in, or one of the tenant's, from one flat Select. */
  const handleTypeChange = (value: string) => {
    const tenant = tenantTypes.find((type) => type.id === value);
    if (tenant) {
      setInteractionType(CUSTOM_INTERACTION_TYPE);
      setTenantTypeId(tenant.id);
    } else {
      setInteractionType(value);
      setTenantTypeId(null);
    }
  };

  /** What the Select is currently bound to: a tenant type's id, or a built-in value. */
  const selectedTypeValue =
    interactionType === CUSTOM_INTERACTION_TYPE && tenantTypeId ? tenantTypeId : interactionType;

  /** The trigger's text, and whether it is TENANT data or OUR OWN copy.
   *
   *  `BidiText` isolates a string whose direction we cannot know, which is true of an
   *  admin-typed label and false of a translated built-in. Its own docstring forbids the
   *  second use, so the branch has to survive to the render — a single wrapper around
   *  both would reorder our copy the day a built-in label opens with a Latin token under
   *  RTL. `InteractionLog` branches the same way for the timeline badge. */
  const selectedType = ((): { label: string; isTenantLabel: boolean } => {
    const tenant = tenantTypes.find((type) => type.id === tenantTypeId);
    if (interactionType === CUSTOM_INTERACTION_TYPE) {
      // `selectableInteractionTypes` keeps the row's own type even when retired, so
      // this normally resolves. The fallback is for the window before the query
      // settles, and for a type the caller genuinely cannot read — never a blank
      // trigger, which reads as an unsaved field.
      return tenant
        ? { label: resolveInteractionTypeLabel(tenant, i18n.language), isTenantLabel: true }
        : { label: t(`interactions.types.${CUSTOM_INTERACTION_TYPE}`), isTenantLabel: false };
    }
    return { label: t(`interactions.types.${interactionType}`), isTenantLabel: false };
  })();
  const [subjectLine, setSubjectLine] = useState(interaction?.subject_line ?? '');
  const [body, setBody] = useState(interaction?.body ?? '');
  const [selectedContactIds, setSelectedContactIds] = useState<string[]>(
    interaction?.contact_ids ?? [],
  );
  const [selectedContactsCache, setSelectedContactsCache] = useState<Contact[]>([]);
  const [autoLinkApproved, setAutoLinkApproved] = useState(true);
  /** UTC ISO instant — LocalizedDateTimeInput renders it in the user's timezone. */
  const [occurredAt, setOccurredAt] = useState<string | null>(
    () => interaction?.occurred_at ?? new Date().toISOString(),
  );

  // Contacts selected from outside the deal — candidates for auto-link.
  const nonDealSelected = useMemo(
    () => selectedContactsCache.filter((c) => !dealContactIds.has(c.id)),
    [selectedContactsCache, dealContactIds]
  );

  const handleContactsChange = (ids: string[], all: Contact[]) => {
    setSelectedContactIds(ids);
    setSelectedContactsCache(all);
  };

  const resetState = () => {
    setInteractionType('note');
    setSubjectLine('');
    setBody('');
    setSelectedContactIds([]);
    setSelectedContactsCache([]);
    setAutoLinkApproved(true);
    setTenantTypeId(null);
    setOccurredAt(new Date().toISOString());
  };

  const linkNonDealContacts = async () => {
    if (!effectiveDealId || !autoLinkApproved || nonDealSelected.length === 0) return;
    const results = await Promise.allSettled(
      nonDealSelected.map((c) =>
        addContactToDeal.mutateAsync({
          dealId: effectiveDealId,
          contactId: c.id,
          role: 'involved',
        })
      )
    );
    const failed = results.filter((r) => r.status === 'rejected').length;
    if (failed > 0) {
      showError(t('interactions.autoLinkPartial', { count: failed }));
    }
  };

  const handleSubmit = async () => {
    try {
      if (isEditMode && interaction) {
        // Send empty strings (not undefined) so cleared fields are actually cleared.
        const data: InteractionUpdate = {
          // The Select offers only MANUAL_INTERACTION_TYPES, and the config flag
          // it derives from (`manual_loggable`) is a subset of the one behind the
          // generated create enum (`api_creatable`) — asserted in
          // modules/master_data/contacts/__tests__/test_interaction_type_single_source.py.
          activity_type: interactionType as InteractionEditableType,
          // Sent on EVERY edit, including as null: the API resolves the pairing
          // against the row as it will BE, so an omitted half would keep the stored
          // type on a row the user just switched back to a built-in.
          interaction_type_id: tenantTypeId,
          subject_line: subjectLine,
          body: body,
          contact_ids: selectedContactIds,
          // Omitted (not nulled) when the input is empty — occurred_at is required
          // on the stored interaction, so a cleared field means "leave it alone".
          occurred_at: occurredAt ?? undefined,
        };
        await editInteraction.mutateAsync({ interactionId: interaction.id, data });
        await linkNonDealContacts();
        showSuccess(t('interactions.updated'));
      } else {
        const data: InteractionCreate = {
          activity_type: interactionType as InteractionCreatableType,
          interaction_type_id: tenantTypeId,
          subject_type: subjectType,
          subject_id: subjectId,
          contact_id: contactId || (subjectType === 'contact' ? subjectId : undefined),
          organization_id: organizationId || (subjectType === 'company' ? subjectId : undefined),
          deal_id: effectiveDealId,
          contact_ids: selectedContactIds.length > 0 ? selectedContactIds : undefined,
          subject_line: subjectLine || undefined,
          body: body || undefined,
          occurred_at: occurredAt ?? undefined,
        };
        await logInteraction.mutateAsync({ data });
        await linkNonDealContacts();
        showSuccess(t('interactions.created'));
      }
      handleClose();
    } catch {
      // The global mutation-error handler shows the specific translated toast.
    }
  };

  const handleClose = () => {
    resetState();
    onClose();
  };

  const showAutoLinkRow = effectiveDealId && nonDealSelected.length > 0;

  return (
    <DialogForm
      open={open}
      onClose={handleClose}
      title={isEditMode ? t('interactions.editInteraction') : t('interactions.addInteraction')}
      onSubmit={handleSubmit}
      submitLabel={t('common:app.save')}
      isLoading={logInteraction.isPending || editInteraction.isPending || addContactToDeal.isPending}
      maxWidth="sm"
    >
      <div className="flex flex-col gap-4">
        <div className="space-y-2">
          <Label>{t('interactions.selectType')}</Label>
          <Select value={selectedTypeValue} onValueChange={handleTypeChange}>
            <SelectTrigger data-testid="interaction-type-select">
              <SelectValue>
                {selectedType.label &&
                  (selectedType.isTenantLabel ? (
                    <BidiText>{selectedType.label}</BidiText>
                  ) : (
                    selectedType.label
                  ))}
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              {/* The built-ins, minus the `custom` DISCRIMINATOR — it names no
                  activity and posting it alone is a row the database rejects. */}
              {OFFERED_BUILTIN_INTERACTION_TYPES.map((type) => (
                <SelectItem key={type} value={type}>
                  {t(`interactions.types.${type}`)}
                </SelectItem>
              ))}
              {/* The tenant's own vocabulary, as its own labelled group so it reads as
                  "ours" beside the platform's rather than as five more built-ins. The
                  group is absent entirely when the tenant has defined none — an empty
                  labelled group is a promise of options that are not there. */}
              {tenantTypes.length > 0 && (
                <SelectGroup>
                  <SelectLabel>{t('interactions.tenantTypesGroup')}</SelectLabel>
                  {tenantTypes.map((type) => (
                    <SelectItem key={type.id} value={type.id}>
                      {/* Tenant-authored, so the string's own direction wins. */}
                      <BidiText>{resolveInteractionTypeLabel(type, i18n.language)}</BidiText>
                    </SelectItem>
                  ))}
                </SelectGroup>
              )}
            </SelectContent>
          </Select>
          {/* Creation is SETTINGS-ONLY. Every CRM surveyed puts it there and none
              offers it inline here, and the reason is sprawl: a vocabulary that feeds
              reporting grows without limit exactly where minting a value is a side
              effect of using one. So this is a pointer, never a create affordance —
              and it is shown only to someone the write routes would admit. */}
          {canManageTypes && (
            <p className="text-sm text-muted-foreground">
              <Link
                to={CONTACTS_MODULE_SETTINGS_PATH}
                className="underline underline-offset-2 hover:text-foreground"
              >
                {t('interactions.manageTypes')}
              </Link>
            </p>
          )}
        </div>

        <div className="space-y-2">
          <Label>{t('interactions.subject')}</Label>
          <Input
            value={subjectLine}
            onChange={(e) => setSubjectLine(e.target.value)}
            placeholder={
              interactionType === 'call'
                ? t('interactions.placeholders.call')
                : interactionType === 'meeting'
                  ? t('interactions.placeholders.meeting')
                  : ''
            }
          />
        </div>

        <div className="space-y-2">
          <Label>{t('interactions.notes')}</Label>
          <CompactRichTextEditor
            value={body}
            onChange={setBody}
          />
        </div>

        <MultiContactPicker
          label={t('interactions.contacts')}
          placeholder={t('interactions.selectContacts')}
          value={selectedContactIds}
          onChange={handleContactsChange}
          dealContacts={effectiveDealId ? (dealContacts ?? []) : undefined}
          organizationId={organizationId ?? null}
          companyName={companyName ?? null}
          allowQuickCreate
          quickCreateRelationships={
            effectiveDealId
              ? dealStageType === 'won'
                ? ['customer']
                : ['lead', 'prospect']
              : undefined
          }
          belowChips={
            showAutoLinkRow ? (
              <label className="flex items-start gap-2 text-sm cursor-pointer text-muted-foreground">
                <Checkbox
                  checked={autoLinkApproved}
                  onCheckedChange={(checked) => setAutoLinkApproved(checked === true)}
                  className="mt-0.5"
                />
                <span className="flex-1">
                  {nonDealSelected.length === 1
                    ? t('interactions.autoLinkOne', {
                        name: getContactDisplayName(nonDealSelected[0]),
                      })
                    : t('interactions.autoLinkMany', { count: nonDealSelected.length })}
                </span>
              </label>
            ) : null
          }
        />

        <LocalizedDateTimeInput
          label={t('interactions.occurredAt')}
          value={occurredAt}
          onChange={setOccurredAt}
        />
      </div>
    </DialogForm>
  );
}
